import { Code, Smartphone, Database, Zap, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import ConsultationModal from '@/components/ConsultationModal';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';

const SoftwareDevelopment = () => {
  const { t } = useLanguage();
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);

  const features = [
    {
      icon: Code,
      title: t('softwareDevelopment.customApplications'),
      description: t('softwareDevelopment.customApplicationsDescription')
    },
    {
      icon: Smartphone,
      title: t('softwareDevelopment.mobileDevelopment'), 
      description: t('softwareDevelopment.mobileDevelopmentDescription')
    },
    {
      icon: Database,
      title: t('softwareDevelopment.enterpriseSystems'),
      description: t('softwareDevelopment.enterpriseSystemsDescription')
    },
    {
      icon: Zap,
      title: t('softwareDevelopment.apiIntegration'),
      description: t('softwareDevelopment.apiIntegrationDescription')
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Back Button */}
      <section className="pt-24 pb-4 px-4">
        <div className="container mx-auto max-w-6xl">
          <Link to="/">
            <Button variant="outline-light" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('jobs.backToHome')}
            </Button>
          </Link>
        </div>
      </section>
      
      {/* Hero Section */}
      <section className="pb-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center animate-fade-in">
            <div className="inline-flex p-6 rounded-3xl bg-gradient-to-br from-primary-glow to-primary mb-8 glow-primary">
              <Code className="w-16 h-16 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-display font-bold mb-6">
              {t('softwareDevelopment.title')}
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-4xl mx-auto mb-8">
              {t('softwareDevelopment.heroDescription')}
            </p>
            <Button 
              size="lg" 
              className="gradient-primary text-lg px-8 py-6 rounded-full glow-primary transition-spring hover:scale-105"
              onClick={() => setIsConsultationOpen(true)}
            >
              {t('softwareDevelopment.startProject')}
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-display font-bold text-center mb-16">
            {t('softwareDevelopment.whatWeBuild')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="group p-6 sm:p-8 rounded-3xl card-gradient border border-border/50 hover:border-primary/50 transition-smooth hover:scale-105 hover:glow-primary animate-fade-in"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="text-center">
                  <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-primary-glow to-primary glow-accent mb-6">
                    <feature.icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-display font-bold mb-4 text-foreground">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-display font-bold text-center mb-16">
            {t('softwareDevelopment.ourTechnologyStack')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { 
                title: t('softwareDevelopment.frontend'), 
                technologies: ["React", "Vue.js", "Angular", "TypeScript", "Tailwind CSS", "Next.js"] 
              },
              { 
                title: t('softwareDevelopment.backend'), 
                technologies: ["Node.js", "Python", "Java", "PostgreSQL", "MongoDB", "Redis"] 
              },
              { 
                title: t('softwareDevelopment.mobileCloud'), 
                technologies: ["React Native", "Flutter", "AWS", "Azure", "Docker", "Kubernetes"] 
              }
            ].map((category, index) => (
              <div 
                key={category.title}
                className="p-6 sm:p-8 rounded-3xl card-gradient border border-border/50 animate-fade-in"
                style={{ animationDelay: `${index * 0.3}s` }}
              >
                <h3 className="text-2xl font-display font-bold mb-6 gradient-text">
                  {category.title}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {category.technologies.map((tech) => (
                    <span 
                      key={tech} 
                      className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm border border-primary/20"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Development Process */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-display font-bold text-center mb-16">
            {t('softwareDevelopment.developmentProcess')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              { step: "01", title: t('softwareDevelopment.discovery'), description: t('softwareDevelopment.discoveryDescription') },
              { step: "02", title: t('softwareDevelopment.design'), description: t('softwareDevelopment.designDescription') },
              { step: "03", title: t('softwareDevelopment.development'), description: t('softwareDevelopment.developmentDescription') },
              { step: "04", title: t('softwareDevelopment.testing'), description: t('softwareDevelopment.testingDescription') },
              { step: "05", title: t('softwareDevelopment.deployment'), description: t('softwareDevelopment.deploymentDescription') }
            ].map((item, index) => (
              <div 
                key={item.step}
                className="text-center animate-fade-in"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="text-3xl font-display font-bold gradient-text mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-display font-bold mb-3">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-display font-bold mb-6">
            {t('softwareDevelopment.readyToBuild')}
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            {t('softwareDevelopment.readyToBuildDescription')}
          </p>
          <Button 
            size="lg" 
            className="gradient-primary text-lg px-8 py-6 rounded-full glow-primary transition-spring hover:scale-105"
            onClick={() => setIsConsultationOpen(true)}
          >
            {t('softwareDevelopment.discussProject')}
          </Button>
        </div>
      </section>

      <Footer />
      
      <ConsultationModal 
        open={isConsultationOpen} 
        onOpenChange={setIsConsultationOpen} 
      />
    </div>
  );
};

export default SoftwareDevelopment;